import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '../../../theme/useTheme';
import { View, Text, ScrollView, TouchableOpacity, Platform, useWindowDimensions } from 'react-native';
import DLONotification, { NotificationVariant } from '../../../modules/dlo/components/DLONotification';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { fetchKfaQuestions, fetchKfaZones, fetchObservationsQuestions, fetchObservationsQuestionsFilter, fetchRackingInspectionQuestions, fetchNhoQuestions, fetchNhoHeaders, fetchSiteEvaluationQuestions, fetchKfaModuleStats, fetchObsCount, fetchNhoCount, fetchRackingCount, fetchSiteEvalCount } from '../../../store/slices/adminSlice';
import { useSelectedLocation } from '../../../hooks';
import { FilterItem } from '../../../components/LHFilter';
import AdminBreadcrumb from '../components/AdminBreadcrumb';
import AdminButton from '../components/AdminButton';
import KfaAddOverlay from './KfaManagement/KfaAddOverlay';
import ObservationAddOverlay from './KfaManagement/ObservationAddOverlay';
import RackingInspectionAddOverlay from './KfaManagement/RackingInspectionAddOverlay';
import NhoAddOverlay from './KfaManagement/NhoAddOverlay';
import SiteEvaluationOverlay from './KfaManagement/SiteEvaluationOverlay';
import KfaModuleSummaryCards from './KfaManagement/KfaModuleSummaryCards';
import KfaTable from './KfaManagement/KfaTable';
import { styles } from './KfaManagement/KfaManagement.styles';
import {
  KfaTab,
  KfaFilterValues,
  EMPTY_FILTERS,
  EDIT_SUCCESS_MESSAGE,
  TAB_MODULE_OPTIONS,
  LOCATION_TYPE_OPTIONS,
  ORG_TYPE_OPTIONS,
  THIRD_PARTY_STATUS_OPTIONS,
  CATEGORY_OPTIONS,
  STATUS_OPTIONS,
  TAB_TO_MODULE,
  KFA_TABS,
} from './KfaManagement/kfaManagementConstants';
import { dispatchWithFilters as _dispatchWithFilters } from './KfaManagement/kfaDispatchWithFilters';

interface KFADashboardProps {
  onBackPress?: () => void;
}


const KFADashboard: React.FC<KFADashboardProps> = ({ onBackPress }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const { width: screenWidth } = useWindowDimensions();
  const isMobilePhone = Platform.OS === 'ios' && screenWidth < 768;
  const dispatch = useDispatch<AppDispatch>();
  const selectedLocation = useSelectedLocation();
  const scrollViewRef = useRef<ScrollView | null>(null);
  const [showAddOverlay, setShowAddOverlay] = useState(false);
  const [showObsAddOverlay, setShowObsAddOverlay] = useState(false);
  const [showRackingAddOverlay, setShowRackingAddOverlay] = useState(false);
  const [showNhoAddOverlay, setShowNhoAddOverlay] = useState(false);
  const [showSiteEvalAddOverlay, setShowSiteEvalAddOverlay] = useState(false);
  const [activeKfaTab, setActiveKfaTab] = useState<KfaTab>('shift-handover');
  const [selectedModuleId, setSelectedModuleId] = useState<string>('shift-handover');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filterValues, setFilterValues] = useState<KfaFilterValues>(EMPTY_FILTERS);
  const [searchText, setSearchText] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const filterDispatchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Page-level notification (shown after overlay closes) ────────────────────────
  const [pageNotification, setPageNotification] = useState<{
    visible: boolean;
    message: string;
    variant: NotificationVariant;
  }>({ visible: false, message: '', variant: 'success' });
  const notificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showPageNotification = (variant: NotificationVariant, message: string) => {
    if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
    scrollViewRef.current?.scrollTo?.({ y: 0, animated: true });
    setPageNotification({ visible: true, message, variant });
    notificationTimerRef.current = setTimeout(
      () => setPageNotification((prev) => ({ ...prev, visible: false })),
      5000,
    );
  };
  const kfaStats = useSelector((state: RootState) => state.configuration.keyFocusAreas.stats);
  const kfaIsLoading = useSelector((state: RootState) => state.configuration.keyFocusAreas.isLoading);
  const kfaStatsLoading = useSelector((state: RootState) => state.configuration.keyFocusAreas.isLoadingStats);
  const totalPages = useSelector((state: RootState) => state.configuration.keyFocusAreas.totalPages);
  const obsTotalPages = useSelector((state: RootState) => state.configuration.observationsQuestions.totalPages);
  const obsIsLoading = useSelector((state: RootState) => state.configuration.observationsQuestions.isLoading);
  const obsStats = useSelector((state: RootState) => state.configuration.observationsQuestions.obsStats);
  const rackingTotalPages = useSelector((state: RootState) => state.configuration.rackingInspection.totalPages);
  const rackingIsLoading = useSelector((state: RootState) => state.configuration.rackingInspection.isLoading);
  const rackingStats = useSelector((state: RootState) => state.configuration.rackingInspection.rackingStats);
  const nhoStats = useSelector((state: RootState) => state.configuration.nhoQuestions.nhoStats);
  const nhoIsLoading = useSelector((state: RootState) => state.configuration.nhoQuestions.isLoading);
  const nhoHeadersLoaded = useSelector((state: RootState) => state.configuration.nhoQuestions.headersLoaded);
  const siteEvalTotalPages = useSelector((state: RootState) => state.configuration.siteEvaluation.totalPages);
  const siteEvalIsLoading = useSelector((state: RootState) => state.configuration.siteEvaluation.isLoading);
  const siteEvalStats = useSelector((state: RootState) => state.configuration.siteEvaluation.siteEvalStats);

  // Only show summary card skeleton on the very first data load, not on pagination changes.
  const [hasDataLoaded, setHasDataLoaded] = useState(false);
  const wasLoading = useRef(false);
  useEffect(() => {
    if (kfaIsLoading) {
      wasLoading.current = true;
    } else if (wasLoading.current) {
      setHasDataLoaded(true);
      wasLoading.current = false;
    }
  }, [kfaIsLoading]);

  const [hasObsDataLoaded, setHasObsDataLoaded] = useState(false);
  const wasObsLoading = useRef(false);
  useEffect(() => {
    if (obsIsLoading) {
      wasObsLoading.current = true;
    } else if (wasObsLoading.current) {
      setHasObsDataLoaded(true);
      // Do NOT lock obsStats here — counts come from fetchObsCount (a separate endpoint)
      // that may resolve after fetchObservationsQuestions. Always read obsStats live.
      wasObsLoading.current = false;
    }
  }, [obsIsLoading]);

  const [hasRackingDataLoaded, setHasRackingDataLoaded] = useState(false);
  const wasRackingLoading = useRef(false);
  useEffect(() => {
    if (rackingIsLoading) {
      wasRackingLoading.current = true;
    } else if (wasRackingLoading.current) {
      setHasRackingDataLoaded(true);
      wasRackingLoading.current = false;
    }
  }, [rackingIsLoading]);

  const [hasNhoDataLoaded, setHasNhoDataLoaded] = useState(false);
  const wasNhoLoading = useRef(false);
  useEffect(() => {
    if (nhoIsLoading) {
      wasNhoLoading.current = true;
    } else if (wasNhoLoading.current) {
      setHasNhoDataLoaded(true);
      wasNhoLoading.current = false;
    }
  }, [nhoIsLoading]);

  const [hasSiteEvalDataLoaded, setHasSiteEvalDataLoaded] = useState(false);
  const wasSiteEvalLoading = useRef(false);
  useEffect(() => {
    if (siteEvalIsLoading) {
      wasSiteEvalLoading.current = true;
    } else if (wasSiteEvalLoading.current) {
      setHasSiteEvalDataLoaded(true);
      wasSiteEvalLoading.current = false;
    }
  }, [siteEvalIsLoading]);

  useEffect(() => {
    dispatch(fetchKfaQuestions({ page: 1, size: pageSize, modules: ['Shift Handover'] }));
    dispatch(fetchKfaModuleStats());
  }, [dispatch]);

  useEffect(() => {
    const locationName =
      (selectedLocation as any)?.displayText ||
      (selectedLocation as any)?.name ||
      '';
    if (locationName) {
      dispatch(fetchKfaZones(locationName));
    }
  }, [dispatch, selectedLocation]);

  const activeTabConfig = KFA_TABS.find((t) => t.key === activeKfaTab)!;

  const activeTabHasMultipleModules = TAB_MODULE_OPTIONS[activeKfaTab].length > 1;

  // Rebuild filter options each time the active tab or filter values change
  const filterOptions: FilterItem[] = useMemo(() => {
    return [
      // Only show Modules filter when the active tab has more than one module
      ...(activeTabHasMultipleModules ? [{
        key: 'modules',
        label: 'Modules',
        selectedOptionId: filterValues.modules,
        options: TAB_MODULE_OPTIONS[activeKfaTab],
      }] : []),
      {
        key: 'locationType',
        label: 'Location Type',
        selectedOptionId: filterValues.locationType,
        options: LOCATION_TYPE_OPTIONS,
      },
      {
        key: 'orgType',
        label: 'Org Type',
        selectedOptionId: filterValues.orgType,
        options: ORG_TYPE_OPTIONS,
      },
      {
        key: 'thirdPartyStatus',
        label: '3PL Status',
        selectedOptionId: filterValues.thirdPartyStatus,
        options: THIRD_PARTY_STATUS_OPTIONS,
      },
      {
        key: 'category',
        label: 'Category',
        selectedOptionId: filterValues.category,
        options: CATEGORY_OPTIONS,
      },
      {
        key: 'status',
        label: 'Status',
        selectedOptionId: filterValues.status,
        options: STATUS_OPTIONS,
      },
    ];
  }, [activeKfaTab, filterValues]);

  const handleTabPress = (tab: typeof KFA_TABS[number]) => {
    const resetFilters = { ...EMPTY_FILTERS };
    setActiveKfaTab(tab.key);
    setSelectedModuleId(tab.moduleIds[0]);
    setCurrentPage(1);
    setFilterValues(resetFilters);
    setSearchText('');
    if (tab.key === 'observations') {
      dispatch(fetchObservationsQuestions({ page: 1, size: pageSize }));
      dispatch(fetchObsCount());
      } else if (tab.key === 'racking-inspection') {
        dispatch(fetchRackingInspectionQuestions({ page: 1, size: pageSize }));
        dispatch(fetchRackingCount());
      } else if (tab.key === 'nho') {
        if (!nhoHeadersLoaded) {
          dispatch(fetchNhoHeaders());
        }
        dispatch(fetchNhoQuestions({ page: 1, size: pageSize }));
        dispatch(fetchNhoCount());
      } else if (tab.key === 'site-evaluation') {
        dispatch(fetchSiteEvaluationQuestions({ page: 1, size: pageSize }));
        dispatch(fetchSiteEvalCount());
      } else if (tab.key === 'shift-handover') {
        dispatch(fetchKfaQuestions({ page: 1, size: pageSize, modules: ['Shift Handover'] }));
        dispatch(fetchKfaModuleStats());
      } else if (tab.key === 'leadership-walk') {
        dispatch(fetchKfaQuestions({ page: 1, size: pageSize, modules: ['Leadership Walk'] }));
        dispatch(fetchKfaModuleStats());
      } else if (tab.key === 'sparc-walk') {
        dispatch(fetchKfaQuestions({ page: 1, size: pageSize, modules: ['SPARC Walk'] }));
        dispatch(fetchKfaModuleStats());
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    _dispatchWithFilters(dispatch, activeKfaTab, page, pageSize, filterValues);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    _dispatchWithFilters(dispatch, activeKfaTab, 1, size, filterValues);
  };

  const handleFilterChange = (key: string, value: any) => {
    const filterValue = value ? (String(value.displayText || value.id || '')) : '';
    const effective = filterValue === 'All' ? '' : filterValue;
    const newFilters = { ...filterValues, [key]: effective };
    setFilterValues(newFilters);
    setCurrentPage(1);
    // Use default pageSize (10) for all filter changes.
    const size = pageSize;
    if (filterDispatchTimeoutRef.current) clearTimeout(filterDispatchTimeoutRef.current);
    filterDispatchTimeoutRef.current = setTimeout(() => {
      _dispatchWithFilters(dispatch, activeKfaTab, 1, size, newFilters, searchText);
    }, 0);
  };

  const handleSearchChange = (text: string) => {
    setSearchText(text);
    setCurrentPage(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    // Clear immediately — no debounce needed when emptying the field
    if (text === '') {
      _dispatchWithFilters(dispatch, activeKfaTab, 1, pageSize, filterValues, '');
    } else {
      searchTimeoutRef.current = setTimeout(() => {
        _dispatchWithFilters(dispatch, activeKfaTab, 1, pageSize, filterValues, text);
      }, 400);
    }
  };

  const handleResetFilters = async () => {
    // Cancel any pending batched filter or search dispatch before firing the single reset call
    if (filterDispatchTimeoutRef.current) clearTimeout(filterDispatchTimeoutRef.current);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setFilterValues(EMPTY_FILTERS);
    setSearchText('');
    setCurrentPage(1);
    if (activeKfaTab === 'observations') {
      dispatch(fetchObservationsQuestions({ page: 1, size: pageSize }));
    } else if (activeKfaTab === 'racking-inspection') {
      dispatch(fetchRackingInspectionQuestions({ page: 1, size: pageSize }));
    } else if (activeKfaTab === 'nho') {
      dispatch(fetchNhoQuestions({ page: 1, size: pageSize }));
    } else if (activeKfaTab === 'site-evaluation') {
      dispatch(fetchSiteEvaluationQuestions({ page: 1, size: pageSize }));
    } else if (activeKfaTab === 'shift-handover') {
      dispatch(fetchKfaQuestions({ page: 1, size: pageSize, modules: ['Shift Handover'] }));
    } else if (activeKfaTab === 'leadership-walk') {
      dispatch(fetchKfaQuestions({ page: 1, size: pageSize, modules: ['Leadership Walk'] }));
    } else if (activeKfaTab === 'sparc-walk') {
      dispatch(fetchKfaQuestions({ page: 1, size: pageSize, modules: ['SPARC Walk'] }));
    }
  };

  return (
    <ScrollView
      ref={scrollViewRef}
      style={[styles.container, { backgroundColor: colors.background.page }]}
      contentContainerStyle={styles.content}
    >

      {/* Page-level notification — appears after NHO (and other) saves complete */}
      <DLONotification
        visible={pageNotification.visible}
        message={pageNotification.message}
        variant={pageNotification.variant}
        onDismiss={() => setPageNotification((prev) => ({ ...prev, visible: false }))}
        style={styles.pageNotification}
      />

      {/* Breadcrumb Navigation */}
      <AdminBreadcrumb
        items={[
          { label: 'Configuration', onPress: onBackPress },
          { label: 'Key Focus Areas' },
        ]}
      />

      {/* Header */}
      <View style={styles.pageHeader}>
        <Text style={[styles.pageTitle, { color: colors.text.primary }]}>Key Focus Areas</Text>
      </View>

      {/* Module Tabs */}
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        style={[styles.kfaTabBar, isDark && { borderBottomColor: colors.border.default }]}
        contentContainerStyle={styles.kfaTabBarContent}
      >
        {KFA_TABS.map((tab) => {
          const isActive = activeKfaTab === tab.key;
          return (
            <TouchableOpacity
              key={tab.key}
              style={[
                styles.kfaTab,
                isActive && (isDark ? { borderBottomColor: colors.brand.primary } : styles.kfaTabActive),
              ]}
              onPress={() => handleTabPress(tab)}
              activeOpacity={0.7}
            >
              <Text
                style={[
                  styles.kfaTabLabel,
                  isDark && { color: colors.text.secondary },
                  isActive && (isDark ? { color: colors.text.primary, fontWeight: '700' } : styles.kfaTabLabelActive),
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Module Summary Cards + Add button row */}
      <View style={[styles.summaryRow, isMobilePhone && styles.summaryRowMobile]}>
        <View style={isMobilePhone ? { width: '100%' } : { flex: 1 }}>
          <KfaModuleSummaryCards
            stats={kfaStats}
            obsStats={obsStats}
            rackingStats={rackingStats}
            nhoStats={nhoStats}
            siteEvalStats={siteEvalStats}
            isLoading={
              activeKfaTab === 'observations' ? !hasObsDataLoaded
              : activeKfaTab === 'racking-inspection' ? !hasRackingDataLoaded
              : activeKfaTab === 'nho' ? !hasNhoDataLoaded
              : activeKfaTab === 'site-evaluation' ? !hasSiteEvalDataLoaded
              : (!hasDataLoaded || kfaStatsLoading)
            }
            visibleModuleIds={activeTabConfig.moduleIds}
          />
        </View>
        <View style={isMobilePhone ? styles.addButtonMobile : { marginTop: 50 }}>
          <AdminButton
            label="+ Add Key Focus Area"
            variant="primary"
            onPress={() => {
              if (activeKfaTab === 'observations') {
                setShowObsAddOverlay(true);
              } else if (activeKfaTab === 'racking-inspection') {
                setShowRackingAddOverlay(true);
              } else if (activeKfaTab === 'nho') {
                setShowNhoAddOverlay(true);
              } else if (activeKfaTab === 'site-evaluation') {
                setShowSiteEvalAddOverlay(true);
              } else {
                setShowAddOverlay(true);
              }
            }}
          />
        </View>
      </View>

      {/* Content */}
      <View
        style={[
          styles.contentBox,
          {
            backgroundColor: colors.background.surface,
            borderColor: colors.border.default,
            borderWidth: isDark ? 1 : 0,
          },
        ]}
      >
        {/* KFA Table */}
        <KfaTable
          activeTab={activeKfaTab}
          currentPage={currentPage}
          totalPages={activeKfaTab === 'observations' ? obsTotalPages : activeKfaTab === 'racking-inspection' ? rackingTotalPages : activeKfaTab === 'nho' ? 1 : activeKfaTab === 'site-evaluation' ? siteEvalTotalPages : totalPages}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          selectedModule={TAB_TO_MODULE[activeKfaTab]}
          searchText={searchText}
          onSearchChange={handleSearchChange}
          onSaveSuccess={() => {
            // Refresh counts after any question edit/save
            if (activeKfaTab === 'observations') {
              dispatch(fetchObsCount());
            } else if (activeKfaTab === 'racking-inspection') {
              dispatch(fetchRackingCount());
            } else if (activeKfaTab === 'nho') {
              dispatch(fetchNhoCount());
            } else if (activeKfaTab === 'site-evaluation') {
              dispatch(fetchSiteEvalCount());
            } else {
              dispatch(fetchKfaModuleStats());
            }
            _dispatchWithFilters(dispatch, activeKfaTab, currentPage, pageSize, filterValues, searchText);
            showPageNotification('success', EDIT_SUCCESS_MESSAGE);
          }}
        />
      </View>

      {/* KFA Add — main / NHO / Racking Inspection / Site Evaluation tabs */}
      <KfaAddOverlay
        visible={showAddOverlay}
        onDismiss={() => setShowAddOverlay(false)}
        selectedModule={TAB_TO_MODULE[activeKfaTab]}
        onSaveSuccess={(moduleNames) => {
          setShowAddOverlay(false);
          dispatch(fetchKfaModuleStats());
          _dispatchWithFilters(dispatch, activeKfaTab, 1, pageSize, filterValues, searchText);
          setCurrentPage(1);
          const names = moduleNames && moduleNames.length > 0 ? moduleNames.join(', ') : '';
          showPageNotification('success', names ? `Key Focus Area created successfully in ${names}` : 'Key Focus Area created successfully');
        }}
      />

      {/* Observation Add — exclusively for the Observations tab */}
      <ObservationAddOverlay
        visible={showObsAddOverlay}
        onDismiss={() => setShowObsAddOverlay(false)}
        mode="create"
        onSaveSuccess={() => {
          setShowObsAddOverlay(false);
          dispatch(fetchObsCount());
          dispatch(fetchObservationsQuestions({ page: 1, size: pageSize }));
          setCurrentPage(1);
          showPageNotification('success', 'Key Focus Area created successfully in Observations');
        }}
      />

      {/* Racking Inspection Add — exclusively for the Racking Inspection tab */}
      <RackingInspectionAddOverlay
        visible={showRackingAddOverlay}
        onDismiss={() => setShowRackingAddOverlay(false)}
        mode="create"
        onSaveSuccess={() => {
          setShowRackingAddOverlay(false);
          dispatch(fetchRackingCount());
          dispatch(fetchRackingInspectionQuestions({ page: 1, size: pageSize }));
          setCurrentPage(1);
          showPageNotification('success', 'Key Focus Area created successfully in Racking Inspection');
        }}
      />

      {/* NHO Add — exclusively for the NHO tab */}
      <NhoAddOverlay
        visible={showNhoAddOverlay}
        onDismiss={() => setShowNhoAddOverlay(false)}
        mode="create"
        onSaveSuccess={() => {
          setShowNhoAddOverlay(false);
          dispatch(fetchNhoCount());
          setCurrentPage(1);
          _dispatchWithFilters(dispatch, activeKfaTab, 1, pageSize, filterValues, searchText);
          showPageNotification('success', 'Key Focus Area created successfully in NHO');
        }}
      />

      <SiteEvaluationOverlay
        visible={showSiteEvalAddOverlay}
        onDismiss={() => setShowSiteEvalAddOverlay(false)}
        mode="create"
        onSaveSuccess={() => {
          setShowSiteEvalAddOverlay(false);
          dispatch(fetchSiteEvalCount());
          setCurrentPage(1);
          _dispatchWithFilters(dispatch, activeKfaTab, 1, pageSize, filterValues, searchText);
          showPageNotification('success', 'Key Focus Area created successfully in Site Evaluation');
        }}
      />
    </ScrollView>
  );
};


export default KFADashboard;
